import React from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';

interface Props {
  label: string;
  score: number;        // 0–10
  previousScore?: number | null;
}

const scoreColor = (score: number): 'success' | 'warning' | 'error' => {
  if (score >= 7) return 'success';
  if (score >= 5) return 'warning';
  return 'error';
};

export const CriteriaScoreBar: React.FC<Props> = ({ label, score, previousScore }) => {
  const pct = (score / 10) * 100;
  const color = scoreColor(score);
  const delta = previousScore != null ? score - previousScore : null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
      {/* Label */}
      <Typography
        variant="caption"
        sx={{ width: 170, flexShrink: 0, color: 'text.secondary', fontWeight: 500 }}
        noWrap
      >
        {label}
      </Typography>

      {/* Bar */}
      <Box sx={{ flex: 1 }}>
        <LinearProgress
          variant="determinate"
          value={pct}
          color={color}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: 'grey.200',
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
              transition: 'transform 0.8s ease',
            },
          }}
        />
      </Box>

      {/* Numeric score */}
      <Typography
        variant="caption"
        fontWeight={700}
        sx={{ width: 26, textAlign: 'right', color: `${color}.main` }}
      >
        {score.toFixed(1)}
      </Typography>

      {/* Delta indicator vs previous attempt */}
      {delta != null && (
        <Typography
          variant="caption"
          sx={{
            width: 36,
            textAlign: 'right',
            fontWeight: 700,
            color: delta > 0 ? 'success.main' : delta < 0 ? 'error.main' : 'text.disabled',
          }}
        >
          {delta > 0 ? `+${delta.toFixed(1)}` : delta < 0 ? delta.toFixed(1) : '—'}
        </Typography>
      )}
    </Box>
  );
};
